/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import {
  FlatList,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SoftPressable } from '../../../shared/ui/SoftPressable';
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import { TicketDeskFooter } from '../../../shared/ticket/TicketDeskFooter'
import { TicketKpiStrip } from '../../../shared/ticket/TicketKpiStrip'
import type { TicketKpiItem } from '../../../shared/ticket/TicketKpiStrip'
import { TicketModuleHeader } from '../../../shared/ticket/TicketModuleHeader'
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ShellScreenLoading } from '../../../shared/layout/ShellScreenLoading';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import type { SupportTicketRow } from '../../../shell/support/services/supportTicketsRepo';
import { SupportTicketDetailPanel } from './SupportTicketDetailPanel';
import { SupportTicketRowCard } from './SupportTicketRowCard';
import { supportStyles as styles } from '../screens/supportStyles';
import { SUPPORT_CREATE_A11Y_LABEL } from '../screens/supportPresentation';
import { spacing } from '../../../shared/theme/spacing';

type Props = {
  rows: SupportTicketRow[];
  selectedTicketId?: string;
  onSelectTicket: (id: string) => void;
  onWriteSupport: () => void;
  refreshing: boolean;
  onRefresh: () => void;
  error: string | null;
  loading?: boolean;
  onTicketUpdated?: (ticket: SupportTicketRow) => void;
  pageIndex: number;
  pageSize: number;
  setPageSize: (size: number) => void;
  hasMore: boolean;
  totalCount: number;
  onLoadNextPage: () => void;
  onLoadPreviousPage: () => void;
};

export function SupportWorkspaceView({
  rows,
  selectedTicketId,
  onSelectTicket,
  onWriteSupport,
  refreshing,
  onRefresh,
  error,
  loading = false,
  onTicketUpdated,
  pageIndex,
  pageSize,
  setPageSize: _setPageSize,
  hasMore,
  totalCount,
  onLoadNextPage,
  onLoadPreviousPage,
}: Props) {
  const kpiCounts = useMemo(() => {
    const submitted = rows.filter((t) => t.status === 'submitted').length;
    const triaged = rows.filter((t) => t.status === 'triaged').length;
    const closed = rows.filter((t) => t.status === 'closed').length;
    return { submitted, triaged, closed };
  }, [rows]);

  const kpiItems = useMemo((): TicketKpiItem[] => [
    { label: 'Total', value: String(totalCount) },
    {
      label: 'Enviados',
      value: String(kpiCounts.submitted),
      tone: kpiCounts.submitted > 0 ? 'warn' : 'default',
    },
    {
      label: 'En curso',
      value: String(kpiCounts.triaged),
      tone: kpiCounts.triaged > 0 ? 'ok' : 'default',
    },
    { label: 'Cerrados', value: String(kpiCounts.closed) },
  ], [totalCount, kpiCounts.submitted, kpiCounts.triaged, kpiCounts.closed]);

  const writeCta = (
    <SoftPressable
      style={styles.softCta}
      onPress={onWriteSupport}
      accessibilityRole="button"
      accessibilityLabel={SUPPORT_CREATE_A11Y_LABEL}
      testID="soporte.write"
    >
      <Ionicons name="headset-outline" size={18} color={ticketColors.brandInk} />
      <Text style={styles.softCtaText}>Escribir a soporte</Text>
    </SoftPressable>
  );

  const pagination = (
    <TicketDeskFooter
      pageIndex={pageIndex}
      pageSize={pageSize}
      productCount={rows.length}
      totalCount={totalCount}
      hasMore={hasMore}
      loading={loading}
      onPreviousPage={onLoadPreviousPage}
      onNextPage={onLoadNextPage}
      embedded
    />
  );

  const refreshControl = (
    <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={ticketColors.ink2} />
  );

  if (!loading && rows.length === 0) {
    return (
      <View style={styles.workspaceRoot}>
        <ScrollView contentContainerStyle={styles.workspaceEmpty} refreshControl={refreshControl}>
          {error ? <Text style={styles.warn}>{error}</Text> : null}
          {!error ? (
            <ModuleEmptyState
              variant="nest"
              kicker="Soporte"
              title="Todavía no escribiste a soporte"
              subtitle="Escribinos y el equipo de Vuelto te responde."
              primaryAction={{ label: 'Escribir a soporte', onPress: onWriteSupport }}
            />
          ) : null}
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.workspaceRoot}>
      <View style={styles.workspaceListPane}>
        {loading ? (
          <ShellScreenLoading label="Cargando reportes…" />
        ) : (
          <>
            {error ? <Text style={styles.warn}>{error}</Text> : null}
            <FlatList
              data={rows}
              keyExtractor={(item) => item.id}
              ListHeaderComponent={
                <View style={workspaceHeaderStyles.headerWrap}>
                  <TicketModuleHeader
                    kicker="Soporte"
                    title="Tus reportes"
                    meta="Seguimiento de tus consultas y las respuestas de Vuelto"
                    trailing={writeCta}
                    testID="soporte.header"
                  />
                  <View style={workspaceHeaderStyles.kpiWrap}>
                    <TicketKpiStrip items={kpiItems} variant="soft" layout="desk" />
                  </View>
                </View>
              }
              refreshControl={refreshControl}
              contentContainerStyle={styles.listContent}
              ListFooterComponent={pagination}
              renderItem={({ item }) => (
                <SupportTicketRowCard
                  item={item}
                  selected={item.id === selectedTicketId}
                  onPress={() => onSelectTicket(item.id)}
                />
              )}
            />
          </>
        )}
      </View>
      <View style={styles.workspaceDetailPane}>
        {selectedTicketId ? (
          <SupportTicketDetailPanel
            ticketId={selectedTicketId}
            initialTicket={rows.find((row) => row.id === selectedTicketId) ?? null}
            active
            scrollable
            workspaceMode
            onTicketUpdated={onTicketUpdated}
          />
        ) : (
          <View style={styles.detailEmpty}>
            <View style={styles.detailEmptyPanel}>
              <View style={styles.detailEmptyIconWrap}>
                <Ionicons name="chatbubbles-outline" size={28} color={ticketColors.gold} />
              </View>
              <Text style={styles.detailEmptyTitle}>Hilo de soporte</Text>
              <Text style={styles.detailEmptyText}>
                Elegí uno de la lista para ver la conversación.
              </Text>
            </View>
          </View>
        )}
      </View>
    </View>
  );
}

const workspaceHeaderStyles = StyleSheet.create({
  headerWrap: {
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  kpiWrap: {
    marginTop: spacing.xs,
  },
});
